import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../../../lib/api";
import type { CurrentUser } from "../../../lib/auth";
import { getThemePref, setThemePref, type ThemePref } from "../../../lib/theme";

export const Route = createFileRoute("/_authed/settings/profile")({
  component: ProfilePage,
});

const _authedRoute = getRouteApi("/_authed");

const inputStyle: React.CSSProperties = {
  padding: "9px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};


function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function resizeToBase64(file: File, maxPx = 300): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = maxPx;
      canvas.height = maxPx;
      const ctx = canvas.getContext("2d")!;
      // fill dark background so transparent PNGs look clean
      ctx.fillStyle = "var(--surface-input)";
      ctx.fillRect(0, 0, maxPx, maxPx);
      // scale to fit (contain), centered
      const scale = Math.min(maxPx / img.width, maxPx / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (maxPx - w) / 2, (maxPx - h) / 2, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.88));
    };
    img.onerror = reject;
    img.src = url;
  });
}

const THEME_OPTIONS: { value: ThemePref; label: string }[] = [
  { value: "light", label: "Claro" },
  { value: "dark", label: "Escuro" },
  { value: "system", label: "Sistema" },
];

function ThemeSelector() {
  const [pref, setPref] = useState<ThemePref>(getThemePref());

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div role="radiogroup" aria-label="Tema" style={{ display: "inline-flex", gap: 0, border: "1px solid var(--line-strong)", borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
        {THEME_OPTIONS.map((opt, i) => {
          const active = pref === opt.value;
          return (
            <button
              key={opt.value}
              role="radio"
              aria-checked={active}
              onClick={() => { setThemePref(opt.value); setPref(opt.value); }}
              style={{
                padding: "7px 16px", fontSize: 12, fontWeight: active ? 600 : 400,
                background: active ? "var(--brand-soft)" : "var(--surface-input)",
                color: active ? "var(--brand)" : "var(--ink-secondary)",
                border: "none",
                borderLeft: i > 0 ? "1px solid var(--line-strong)" : "none",
                cursor: "pointer", transition: "background 0.15s, color 0.15s",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
        Aplicado na hora, só neste navegador. "Sistema" acompanha o modo do seu dispositivo.
      </span>
    </div>
  );
}

function ProfilePage() {
  const ctx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const currentUser = ctx.user!;
  const qc = useQueryClient();

  const [name, setName] = useState(currentUser.name);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(currentUser.photo_url ?? null);
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(null);
  const [passError, setPassError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const mut = useMutation({
    mutationFn: async (payload: { name?: string; password?: string; photo_url?: string }) =>
      api.patch("/auth/me", payload),
    onSuccess: async () => {
      setPendingPhoto(null);
      setNewPass("");
      setConfirmPass("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      // Invalida o contexto do usuário para atualizar nome/foto no sidebar
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await resizeToBase64(file);
      setPhotoPreview(b64);
      setPendingPhoto(b64);
    } catch {
      // ignore
    }
    e.target.value = "";
  }

  function passChecks(p: string) {
    return {
      length: p.length >= 10,
      upper: /[A-Z]/.test(p),
      digit: /[0-9]/.test(p),
      special: /[!@#$%^&*()_+\-=\[\]{};:'",.<>/?\\|`~]/.test(p),
    };
  }

  const checks = passChecks(newPass);
  const passValid = !newPass || Object.values(checks).every(Boolean);

  function handleSave() {
    setPassError(null);
    if (newPass && newPass !== confirmPass) {
      setPassError("As senhas não coincidem.");
      return;
    }
    if (newPass && !Object.values(checks).every(Boolean)) {
      setPassError("A senha não atende aos requisitos.");
      return;
    }
    const payload: Record<string, string> = {};
    if (name !== currentUser.name) payload.name = name;
    if (newPass) payload.password = newPass;
    if (pendingPhoto) payload.photo_url = pendingPhoto;
    if (Object.keys(payload).length === 0) return;
    mut.mutate(payload);
  }

  const hasChanges =
    (name !== currentUser.name || (!!newPass && passValid) || !!pendingPhoto);

  return (
    <div style={{ maxWidth: 560, display: "flex", flexDirection: "column", gap: 32 }}>

      {/* Foto + identidade */}
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>

        {/* Avatar clicável */}
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            width: 88, height: 88, borderRadius: "50%",
            background: "var(--surface-input)", border: "2px solid var(--line-strong)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", overflow: "hidden", flexShrink: 0,
            position: "relative",
          }}
          title="Clique para trocar a foto"
        >
          {photoPreview ? (
            <img src={photoPreview} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 28, fontWeight: 700, color: "var(--ink-muted)", userSelect: "none" }}>
              {initials(currentUser.name)}
            </span>
          )}
          {/* Overlay hover */}
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0, transition: "opacity 0.15s",
          }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "0")}
          >
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 600 }}>Trocar foto</span>
          </div>
        </div>

        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />

        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>{currentUser.name}</div>
          <span style={{
            display: "inline-block", marginTop: 8,
            fontSize: 11, padding: "2px 8px", borderRadius: 99,
            background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)",
          }}>
            {currentUser.role_name}
          </span>
        </div>
      </div>

      {/* Seção: dados */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Dados do perfil
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Nome</label>
          <input
            style={inputStyle}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Seu nome completo"
          />
        </div>

      </div>

      {/* Seção: aparência (preferência local — aplica na hora, não entra no Salvar) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Aparência
        </div>
        <ThemeSelector />
      </div>

      {/* Seção: senha */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Alterar senha
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Nova senha</label>
          <input
            type="password"
            style={inputStyle}
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            placeholder="Deixe em branco para não alterar"
          />
        </div>
        {newPass && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "10px 12px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8 }}>
            {([
              [checks.length, "Mínimo 10 caracteres"],
              [checks.upper, "Uma letra maiúscula"],
              [checks.digit, "Um número"],
              [checks.special, "Um caractere especial (!@#$%...)"],
            ] as [boolean, string][]).map(([ok, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: ok ? "var(--ok)" : "var(--ink-muted)", lineHeight: 1 }}>{ok ? "✓" : "○"}</span>
                <span style={{ fontSize: 12, color: ok ? "var(--ok)" : "var(--ink-muted)" }}>{label}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Confirmar nova senha</label>
          <input
            type="password"
            style={inputStyle}
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            placeholder="Repita a nova senha"
          />
        </div>
        {passError && (
          <span style={{ fontSize: 12, color: "var(--danger)" }}>{passError}</span>
        )}
      </div>

      {/* Rodapé */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!hasChanges || mut.isPending}
          style={{
            background: hasChanges && !mut.isPending ? "var(--brand)" : "var(--line-strong)",
            color: hasChanges && !mut.isPending ? "#fff" : "var(--ink-dim)",
            border: "none", borderRadius: 8, padding: "9px 24px",
            fontSize: 13, fontWeight: 600, cursor: hasChanges ? "pointer" : "default",
            transition: "background 0.15s",
          }}
        >
          {mut.isPending ? "Salvando..." : "Salvar alterações"}
        </button>
        {saved && (
          <span style={{ fontSize: 13, color: "var(--ok)" }}>Salvo!</span>
        )}
        {mut.isError && (
          <span style={{ fontSize: 13, color: "var(--danger)" }}>Erro ao salvar.</span>
        )}
      </div>

    </div>
  );
}
